import { useEffect, useRef } from 'react';
import { caseApiService } from '../services/caseApiService';
import { useVisibilityAwarePolling } from './useVisibilityAwarePolling';
import { getTabId } from '../utils/clientIdentifiers';
import { announceTakeover, announceReleased, subscribeToPresence } from '../utils/caseEditingPresenceChannel';

/** Passed to `onTakenOver` when another tab's marker replaces yours on the case you're editing. */
export interface CaseTakenOverBy {
    userId: string;
    tabId: string;
    firstName?: string;
    lastName?: string;
}

export interface CaseEditingLockOptions {
    /** The current user's id -- included in presence broadcasts so peers can name the taker. */
    currentUserId?: string;
    currentUserFirstName?: string;
    currentUserLastName?: string;
    /**
     * Fired when another TAB now holds the marker for the case you're editing -- whether a
     * teammate or another tab/window of your own. Detected instantly via BroadcastChannel for
     * same-browser tabs, and within the poll interval via the server heartbeat otherwise.
     */
    onTakenOver?: (by: CaseTakenOverBy) => void;
    /**
     * Fired each poll with whether a link-token client is currently active in the expense portal
     * for this case (ConcurrentCaseEditGuard A2). Drives the attorney's "a client is entering
     * expenses" indicator.
     */
    onClientEditingChange?: (active: boolean) => void;
    /**
     * Gates the presence poll + channel takeover detection (not the marker lifecycle). Pass false
     * to pause detection -- e.g. while the takeover modal is already showing. Default true.
     */
    pollEnabled?: boolean;
    /** Presence poll interval in ms. Default 15000. */
    pollIntervalMs?: number;
}

/**
 * ConcurrentCaseEditGuard: marks a case as "currently being edited by me (this tab)" while the
 * wizard has it open, and clears it on the way out. Informational only (see
 * docs/AUTOSAVE_DETAILS.md §17) -- not a lock; the homepage/other tabs just warn.
 *
 * - Marks on mount / caseId change; clears on unmount, re-mark, and `pagehide` (keepalive beacon).
 *   Marks/clears now carry this tab's id (getTabId), so the marker identifies the holding TAB, not
 *   just the user -- and the clear is tab-scoped so closing one tab never wipes another's marker.
 * - Live takeover detection (opt-in via `opts.onTakenOver`):
 *   * Same-browser: broadcasts `announceTakeover` on every acquire and subscribes to peers'
 *     broadcasts -- a peer acquiring the case you hold fires `onTakenOver` instantly, server-free.
 *   * Cross-browser / crash: a visibility-gated ~15s poll of `heartbeatEditing` refreshes this
 *     tab's server lease AND returns the current holder; if a different tab id holds it,
 *     `onTakenOver` fires. Abandoned markers expire via the server's ~60s TTL.
 *
 * @param caseId - The case currently loaded in the wizard, or undefined if none/not yet loaded.
 * @param enabled - Feature flag on AND the case is editable (read-only opens never mark).
 */
export function useCaseEditingLock(
    caseId: string | undefined,
    enabled: boolean,
    opts?: CaseEditingLockOptions
): void {
    const {
        currentUserId,
        currentUserFirstName,
        currentUserLastName,
        onTakenOver,
        onClientEditingChange,
        pollEnabled = true,
        pollIntervalMs = 15000,
    } = opts ?? {};

    // Identity/callback held in refs so the marker effect stays keyed on just [caseId, enabled]
    // (identity is stable once auth loads; we don't want it to churn the mark/clear lifecycle).
    const identityRef = useRef({ currentUserId, currentUserFirstName, currentUserLastName });
    identityRef.current = { currentUserId, currentUserFirstName, currentUserLastName };
    const onTakenOverRef = useRef(onTakenOver);
    onTakenOverRef.current = onTakenOver;
    const onClientEditingChangeRef = useRef(onClientEditingChange);
    onClientEditingChangeRef.current = onClientEditingChange;

    // Marker lifecycle + same-browser "I acquired this" broadcast.
    useEffect(() => {
        if (!enabled || !caseId) return;
        const tabId = getTabId();
        const { currentUserId: uid, currentUserFirstName: fn, currentUserLastName: ln } = identityRef.current;

        caseApiService.markEditing(caseId);
        announceTakeover(caseId, tabId, uid, fn, ln);

        const handlePageHide = () => {
            caseApiService.clearEditingBeacon(caseId);
        };
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            announceReleased(caseId, tabId);
            caseApiService.clearEditing(caseId);
        };
    }, [caseId, enabled]);

    const detectionEnabled = enabled && pollEnabled && !!caseId && (!!onTakenOver || !!onClientEditingChange);

    // Same-browser instant takeover: a peer tab that just acquired MY case (different tab id).
    useEffect(() => {
        if (!detectionEnabled || !caseId) return;
        const myTabId = getTabId();
        const unsubscribe = subscribeToPresence((msg) => {
            if (msg.type === 'takeover' && msg.caseId === caseId && msg.tabId !== myTabId) {
                onTakenOverRef.current?.({
                    userId: msg.userId ?? '',
                    tabId: msg.tabId,
                    firstName: msg.firstName,
                    lastName: msg.lastName,
                });
            }
        });
        return unsubscribe;
    }, [caseId, detectionEnabled]);

    // Cross-browser / lease-refresh: heartbeat this tab's lease and detect a different holder.
    useVisibilityAwarePolling(
        () => {
            if (!caseId) return;
            const myTabId = getTabId();
            caseApiService.heartbeatEditing(caseId).then((status) => {
                if (status?.edbytab && status.edbytab !== myTabId) {
                    onTakenOverRef.current?.({
                        userId: status.edby ?? '',
                        tabId: status.edbytab,
                        firstName: status.edbyfn,
                        lastName: status.edbyln,
                    });
                }
                onClientEditingChangeRef.current?.(status?.clientEditing === true);
            });
        },
        pollIntervalMs,
        { enabled: detectionEnabled, runOnVisible: true }
    );
}
